import { RefObject, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';

import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { errorAlert } from 'utils/alert';

/**
 * Confirmation prompt for deleting a chat thread, shared by the three surfaces that offer it. What
 * deleting means is the caller's business — the floating panel only has to drop the thread, while
 * the full page also has a URL and a remembered thread to clean up — so this owns the prompt and
 * the failure alert, nothing else.
 */
export const useAiChatDeleteThreadPrompt = (
    onConfirmDelete: (threadId: number) => Promise<void>,
    // A surface painting above the app's dialog tier has the prompt scoped inside it, so the prompt's
    // backdrop covers it.
    layer?: { container?: RefObject<HTMLElement | null> }
) => {
    const intl = useIntl();
    const { openModal, closeModal } = useModal();
    // The confirmation dialog has no busy state, so a second click would fire a second DELETE and
    // fail on a thread that is already gone.
    const isDeletingRef = useRef(false);

    return useCallback(
        (threadId: number) => {
            openModal(
                <ConfirmationModal
                    titleId="ai-chat.delete-thread-title"
                    onCancel={closeModal}
                    onConfirm={() => {
                        if (isDeletingRef.current) {
                            return;
                        }

                        isDeletingRef.current = true;
                        void (async () => {
                            try {
                                await onConfirmDelete(threadId);
                                closeModal();
                            } catch (deleteError) {
                                console.error('Failed to delete thread:', deleteError);
                                errorAlert(intl.formatMessage({ id: 'ai-chat.delete-thread-error' }));
                            } finally {
                                isDeletingRef.current = false;
                            }
                        })();
                    }}
                    content={
                        <p className="text-body1">{intl.formatMessage({ id: 'ai-chat.delete-thread-confirm' })}</p>
                    }
                    cancelId="no"
                />,
                undefined,
                undefined,
                layer?.container
            );
        },
        [closeModal, intl, layer?.container, onConfirmDelete, openModal]
    );
};
